/**
 * LoreEditor — DM-only side panel for editing a single lore note.
 *
 * Loads the raw markdown source for the selected slug (frontmatter + body,
 * un-stripped — the `getNote` read endpoint returns a player-safe view that's
 * useless for editing) and exposes three editing surfaces:
 *
 *   1. A plain `<textarea>` for the body. Debounced autosave to a draft in
 *      DynamoDB on every keystroke; no auto-write to S3 ever — only the
 *      explicit Publish button promotes a draft to the vault.
 *
 *   2. A frontmatter form (visibility, reveal date, summary, tags). Each
 *      field calls `transformFrontmatter` on the server, which uses the
 *      Domain-layer line-based YAML editor — comments, ordering, quoting,
 *      and unknown keys are preserved. The transform returns a NEW whole
 *      markdown string which we drop straight back into the textarea.
 *
 *   3. A selection-based section toolbar. The DM selects text in the body
 *      textarea and clicks "Wrap in players block" or "Wrap in reveal block".
 *      We convert the selection's char offsets to body-local line indices
 *      (the body has had its frontmatter stripped at conversion time, so the
 *      indices line up with what `LoreSectionWrapper` expects), call
 *      `transformSection`, and the wrapper snaps the selection outward to
 *      the nearest blank lines and refuses if it'd straddle a code fence.
 *
 * Conflict handling: both `putNote` and `publishDraft` throw `LoreConflictError`
 * on a 412 from S3 (vault changed under us). We catch it, surface the current
 * vault content, and let the DM pick "use mine" / "use vault" / "merge by hand
 * in textarea". A 3-way merge UI is overkill for this slice.
 *
 * The editor mounts as a child of the right sidebar in `Lore.tsx`. The parent
 * controls when it's visible via the `slug` prop; passing `null` collapses it.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import {
  deleteDraft,
  FrontmatterPatch,
  getDraft,
  getNoteSource,
  LoreConflict,
  LoreConflictError,
  LoreSource,
  publishDraft,
  putDraft,
  putNote,
  SectionWrap,
  transformFrontmatter,
  transformSection,
} from '../lib/loreApi';
import './LoreEditor.css';

/**
 * Obsidian-sync debounce window for writes. If the last observed S3 etag moved
 * within this many ms, block publish/save-direct and wait for the window to
 * clear before issuing the write. Gives Obsidian's background vault sync room
 * to settle so we stop 412'ing on inflight sync races. See `waitForSyncWindow`.
 */
const SYNC_DEBOUNCE_MS = 30_000;

interface Props {
  /** Currently-selected note slug, or null to hide the editor entirely. */
  slug: string | null;
  /** Player folder names from `players.json`, used to populate the visibility multiselect. */
  knownPlayers: string[];
  /** Called when the editor publishes a note, so the parent can refresh the manifest + reload the read view. */
  onPublished: () => void;
}

interface FrontmatterParsed {
  raw: string;
  body: string;
  frontmatter: {
    players?: string[];          // explicit list (or empty for "none")
    playersAll?: boolean;
    playersNone?: boolean;
    reveal?: string;
    summary?: string;
    tags?: string[];
  };
}

/**
 * Lightweight client-side frontmatter parser. We DO NOT round-trip through
 * this — every mutation goes server-side via `transformFrontmatter` so the
 * authoritative line-based writer in C# preserves comments / ordering /
 * quoting. This parser only extracts the values needed to populate the form
 * controls, and is intentionally tolerant of malformed input.
 */
function parseSource(raw: string): FrontmatterParsed {
  // Match a leading `---\n...---\n` block. Empty body is fine.
  const m = raw.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!m) {
    return { raw, body: raw, frontmatter: {} };
  }
  const yaml = m[1];
  const body = m[2];
  const fm: FrontmatterParsed['frontmatter'] = {};

  // Walk lines. We only handle `key: value` and the tiny subset of YAML the
  // lore feature actually uses (flow lists for players/tags, scalars for
  // reveal/summary).
  for (const line of yaml.split('\n')) {
    const idx = line.indexOf(':');
    if (idx < 0) continue;
    const key = line.slice(0, idx).trim();
    const value = line.slice(idx + 1).trim();
    if (!key) continue;
    if (key === 'players') {
      if (value === 'all') { fm.playersAll = true; continue; }
      if (value === '[]' || value === '') { fm.playersNone = true; continue; }
      // Flow list: [a, b, c] or [a, "b c"]. Strip brackets, split, trim, dequote.
      if (value.startsWith('[') && value.endsWith(']')) {
        const inner = value.slice(1, -1);
        fm.players = inner
          .split(',')
          .map((s) => s.trim().replace(/^["']|["']$/g, ''))
          .filter((s) => s.length > 0);
        continue;
      }
      // Single string fallback.
      fm.players = [value.replace(/^["']|["']$/g, '')];
    } else if (key === 'reveal') {
      fm.reveal = value;
    } else if (key === 'summary') {
      fm.summary = value.replace(/^["']|["']$/g, '');
    } else if (key === 'tags') {
      if (value.startsWith('[') && value.endsWith(']')) {
        const inner = value.slice(1, -1);
        fm.tags = inner
          .split(',')
          .map((s) => s.trim().replace(/^["']|["']$/g, ''))
          .filter((s) => s.length > 0);
      } else if (value) {
        fm.tags = [value];
      }
    }
  }

  return { raw, body, frontmatter: fm };
}

/**
 * Convert a textarea char offset into a 0-based line index inside the BODY
 * (frontmatter excluded). The body string passed in is the post-frontmatter
 * portion of `raw`, so the offset arg here is already body-local.
 */
function offsetToLine(body: string, offset: number): number {
  let line = 0;
  for (let i = 0; i < offset && i < body.length; i++) {
    if (body[i] === '\n') line++;
  }
  return line;
}

/**
 * Splits a raw source string into its frontmatter prefix and body suffix so
 * the textarea-shown body offsets line up with `LoreSectionWrapper` line
 * indices. If the source has no frontmatter, prefix is empty and the body is
 * the whole string.
 */
function splitFrontmatter(raw: string): { prefix: string; body: string } {
  const m = raw.match(/^---\n[\s\S]*?\n---\n?/);
  if (!m) return { prefix: '', body: raw };
  return { prefix: m[0], body: raw.slice(m[0].length) };
}

export default function LoreEditor({ slug, knownPlayers, onPublished }: Props) {
  const [source, setSource] = useState<LoreSource | null>(null);
  const [text, setText] = useState<string>('');         // current editor buffer (whole source)
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);  // human label of in-flight op
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<LoreConflict | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const debounceRef = useRef<number | null>(null);
  // Track which slug the current `text` belongs to so a slow draft save from
  // an old slug doesn't bleed into the newly-selected one.
  const textOwnerRef = useRef<string | null>(null);
  // Wall-clock of the most recent moment we observed this note's S3 etag —
  // from a fresh `getNoteSource`, a successful `putDraft`, or the `currentEtag`
  // surfaced in a 412 conflict. Drives the Obsidian-sync write debounce. Lives
  // in a ref so `waitForSyncWindow` can read the latest value without being
  // recreated on every bump (which would cascade into publish/save-direct deps).
  const lastObservedETagAtRef = useRef<number | null>(null);

  /**
   * If the last observed S3 etag moved too recently, block the write and wait
   * for the `SYNC_DEBOUNCE_MS` window to clear. Shows a static "Obsidian synced
   * this note Ns ago, waiting…" busy message for the duration. Caller is
   * expected to overwrite `busy` with its own label afterwards (e.g. "Publishing…").
   *
   * No-op if we haven't observed an etag yet (first paint before source load
   * finishes) or if the window has already elapsed.
   */
  const waitForSyncWindow = useCallback(async () => {
    const observed = lastObservedETagAtRef.current;
    if (observed == null) return;
    const delta = Date.now() - observed;
    if (delta >= SYNC_DEBOUNCE_MS) return;
    const ago = Math.max(1, Math.round(delta / 1000));
    const wait = SYNC_DEBOUNCE_MS - delta;
    setBusy(`Obsidian synced this note ${ago}s ago, waiting…`);
    await new Promise<void>((resolve) => window.setTimeout(resolve, wait));
  }, []);

  // Load source + any existing draft when slug changes.
  useEffect(() => {
    if (!slug) {
      setSource(null);
      setText('');
      setHasDraft(false);
      setError(null);
      setConflict(null);
      setSavedAt(null);
      textOwnerRef.current = null;
      lastObservedETagAtRef.current = null;
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setError(null);
    setConflict(null);

    (async () => {
      try {
        // Source first — gives us the etag and the canonical body to fall
        // back to if there's no draft.
        const src = await getNoteSource(slug);
        if (cancelled) return;
        setSource(src);
        lastObservedETagAtRef.current = Date.now();

        // Then check for an in-flight draft. If present, the editor opens
        // ON the draft text rather than the canonical S3 body.
        const draft = await getDraft(slug);
        if (cancelled) return;
        if (draft) {
          setText(draft.content);
          setHasDraft(true);
          setSavedAt(draft.updatedAt);
        } else {
          setText(src.content);
          setHasDraft(false);
          setSavedAt(null);
        }
        textOwnerRef.current = slug;
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      // Cancel any pending debounce — its captured slug is now stale.
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [slug]);

  // Debounced draft autosave on every text change (1s after the last keystroke).
  // We never write to S3 from this hook — only the explicit Publish button does.
  const scheduleAutosave = useCallback(
    (nextText: string) => {
      if (!slug || !source) return;
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(async () => {
        debounceRef.current = null;
        // Belt and braces: ignore stale fires from a previous slug.
        if (textOwnerRef.current !== slug) return;
        try {
          const result = await putDraft(slug, nextText, source.etag);
          setHasDraft(true);
          setSavedAt(result.savedAt);
          // Bump the sync-debounce clock on every successful draft sync event —
          // an active draft cadence means we've just reaffirmed the current
          // baseEtag with the server and should give Obsidian another full
          // window before letting a publish/save-direct race against it.
          lastObservedETagAtRef.current = Date.now();
          setError(null);
        } catch (e) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }, 1000);
    },
    [slug, source],
  );

  const onTextChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      setText(e.target.value);
      scheduleAutosave(e.target.value);
    },
    [scheduleAutosave],
  );

  // Replace the editor buffer with a server-transformed version, and queue
  // an autosave so the new state is durable. Used by the frontmatter form
  // and the section toolbar.
  const replaceText = useCallback(
    (next: string) => {
      setText(next);
      scheduleAutosave(next);
    },
    [scheduleAutosave],
  );

  const fm = useMemo(() => parseSource(text), [text]);

  const applyFrontmatter = useCallback(
    async (patch: FrontmatterPatch) => {
      if (!slug || !source) return;
      setBusy('Applying frontmatter…');
      setError(null);
      try {
        const r = await transformFrontmatter(text, patch);
        replaceText(r.body);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [slug, source, text, replaceText],
  );

  const wrapSelection = useCallback(
    async (kind: 'players' | 'reveal') => {
      if (!slug || !source) return;
      const ta = textareaRef.current;
      if (!ta) return;
      const { prefix, body } = splitFrontmatter(text);
      // Selection offsets are into the WHOLE textarea string. Convert to
      // body-local offsets by subtracting the prefix length, clamping at 0.
      const start = Math.max(0, ta.selectionStart - prefix.length);
      const end = Math.max(start, ta.selectionEnd - prefix.length);
      if (end <= start) {
        setError('Select some text to wrap first.');
        return;
      }
      const startLine = offsetToLine(body, start);
      // For an inclusive end-line, use the line containing the LAST character
      // of the selection. If the selection ends at the start of a line, the
      // wrapper would otherwise pull in an extra empty line.
      const endLine = offsetToLine(body, Math.max(start, end - 1));

      let section: SectionWrap;
      if (kind === 'players') {
        const list = window.prompt(
          'Players (comma-separated, or leave blank for "all"):',
          '',
        );
        if (list === null) return;
        section = {
          wrapKind: 'players',
          startLine,
          endLine,
          playersList: list.trim() || undefined,
        };
      } else {
        const date = window.prompt('Reveal date (YYYY-MM-DD):', '');
        if (date === null) return;
        if (!/^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
          setError('Reveal date must be YYYY-MM-DD.');
          return;
        }
        section = {
          wrapKind: 'reveal',
          startLine,
          endLine,
          revealDate: date.trim(),
        };
      }

      setBusy('Wrapping section…');
      setError(null);
      try {
        const r = await transformSection(body, section);
        replaceText(prefix + r.body);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(null);
      }
    },
    [slug, source, text, replaceText],
  );

  const onPublish = useCallback(async () => {
    if (!slug || !source) return;
    setError(null);
    setConflict(null);
    // Obsidian-sync debounce: wait out any in-flight vault sync window before
    // issuing the S3 write. No-op if we're already outside the window.
    await waitForSyncWindow();
    setBusy('Publishing…');
    try {
      // Make sure the latest text is in the draft before we ask the server to
      // publish from the draft. Cancel any pending debounce so we don't race.
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      await putDraft(slug, text, source.etag);
      setHasDraft(true);

      await publishDraft(slug);
      // Reload source so the editor's etag matches what's now in S3, and the
      // draft state is cleared (the server deletes it on publish).
      const fresh = await getNoteSource(slug);
      setSource(fresh);
      setText(fresh.content);
      setHasDraft(false);
      setSavedAt(null);
      lastObservedETagAtRef.current = Date.now();
      onPublished();
    } catch (e) {
      if (e instanceof LoreConflictError) {
        setConflict(e.conflict);
        // We just learned the vault etag moved — reset the sync window so the
        // next save attempt waits another full debounce.
        lastObservedETagAtRef.current = Date.now();
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  }, [slug, source, text, onPublished, waitForSyncWindow]);

  const onSaveDirect = useCallback(async () => {
    if (!slug || !source) return;
    setError(null);
    setConflict(null);
    await waitForSyncWindow();
    setBusy('Saving to vault…');
    try {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      await putNote(slug, text, source.etag);
      // Mirror publish: re-fetch the canonical source so the etag matches.
      const fresh = await getNoteSource(slug);
      setSource(fresh);
      setText(fresh.content);
      setHasDraft(false);
      setSavedAt(null);
      lastObservedETagAtRef.current = Date.now();
      onPublished();
    } catch (e) {
      if (e instanceof LoreConflictError) {
        setConflict(e.conflict);
        lastObservedETagAtRef.current = Date.now();
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(null);
    }
  }, [slug, source, text, onPublished, waitForSyncWindow]);

  const onDiscardDraft = useCallback(async () => {
    if (!slug || !source) return;
    if (!window.confirm('Discard the draft and revert to the vault copy?')) return;
    setBusy('Discarding draft…');
    setError(null);
    try {
      await deleteDraft(slug);
      setText(source.content);
      setHasDraft(false);
      setSavedAt(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [slug, source]);

  const onAcceptVaultConflict = useCallback(() => {
    if (!conflict) return;
    setText(conflict.currentBody);
    if (source) {
      setSource({ ...source, etag: conflict.currentEtag, content: conflict.currentBody });
    }
    setConflict(null);
  }, [conflict, source]);

  if (!slug) {
    return (
      <section className="lore-editor lore-editor--empty">
        <h3>Editor</h3>
        <p className="lore-meta__hint">Select a note to edit.</p>
      </section>
    );
  }

  return (
    <section className="lore-editor">
      <header className="lore-editor__header">
        <h3>Edit</h3>
        <div className="lore-editor__status">
          {loading && <span>Loading…</span>}
          {!loading && hasDraft && <span className="lore-editor__draft-badge">draft</span>}
          {!loading && savedAt && (
            <span className="lore-editor__saved">
              saved {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}
          {busy && <span className="lore-editor__busy">{busy}</span>}
        </div>
      </header>

      {loadError && <div className="lore-error">{loadError}</div>}
      {error && <div className="lore-error">{error}</div>}

      {conflict && (
        <div className="lore-editor__conflict">
          <strong>Vault changed under you.</strong>
          <p>
            The vault copy of this note was modified after your draft was loaded.
            Choose how to resolve:
          </p>
          <button type="button" onClick={onAcceptVaultConflict}>
            Replace editor with vault copy
          </button>
          <button type="button" onClick={() => setConflict(null)}>
            Keep editing my version
          </button>
        </div>
      )}

      {source && !loadError && (
        <>
          <div className="lore-editor__toolbar">
            <button
              type="button"
              disabled={!!busy}
              onClick={() => wrapSelection('players')}
              title="Wrap the selected text in a %% players: ... %% block"
            >
              Wrap players
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => wrapSelection('reveal')}
              title="Wrap the selected text in a %% reveal: yyyy-mm-dd %% block"
            >
              Wrap reveal
            </button>
          </div>

          <textarea
            ref={textareaRef}
            className="lore-editor__textarea"
            value={text}
            onChange={onTextChange}
            spellCheck={false}
            disabled={!!busy && busy !== 'Applying frontmatter…' && busy !== 'Wrapping section…'}
          />

          <fieldset className="lore-editor__frontmatter">
            <legend>Frontmatter</legend>

            <label>
              <span>Visibility</span>
              <select
                value={
                  fm.frontmatter.playersAll
                    ? 'all'
                    : fm.frontmatter.playersNone
                      ? 'none'
                      : fm.frontmatter.players && fm.frontmatter.players.length > 0
                        ? 'list'
                        : 'default'
                }
                disabled={!!busy}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === 'default') {
                    void applyFrontmatter({ setPlayers: true, players: null });
                  } else if (v === 'all') {
                    void applyFrontmatter({ setPlayers: true, players: { all: true } });
                  } else if (v === 'none') {
                    void applyFrontmatter({ setPlayers: true, players: { none: true } });
                  } else {
                    void applyFrontmatter({
                      setPlayers: true,
                      players: { allowed: fm.frontmatter.players ?? [] },
                    });
                  }
                }}
              >
                <option value="default">DM only (default)</option>
                <option value="all">All players</option>
                <option value="none">DM only (explicit)</option>
                <option value="list">Specific players…</option>
              </select>
            </label>

            {(fm.frontmatter.players !== undefined && !fm.frontmatter.playersAll && !fm.frontmatter.playersNone) && (
              <div className="lore-editor__players">
                {knownPlayers.map((p) => {
                  const checked = fm.frontmatter.players?.includes(p) ?? false;
                  return (
                    <label key={p}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!!busy}
                        onChange={(e) => {
                          const current = new Set(fm.frontmatter.players ?? []);
                          if (e.target.checked) current.add(p);
                          else current.delete(p);
                          void applyFrontmatter({
                            setPlayers: true,
                            players: { allowed: [...current] },
                          });
                        }}
                      />
                      {p}
                    </label>
                  );
                })}
                {knownPlayers.length === 0 && (
                  <p className="lore-meta__hint">No players configured in players.json.</p>
                )}
              </div>
            )}

            <label>
              <span>Reveal date</span>
              <input
                type="date"
                value={fm.frontmatter.reveal ?? ''}
                disabled={!!busy}
                onChange={(e) =>
                  void applyFrontmatter({
                    setReveal: true,
                    reveal: e.target.value || null,
                  })
                }
              />
            </label>

            <label>
              <span>Summary</span>
              {/* Uncontrolled with a key tied to the parsed value: when the
                  textarea body changes (or a different note loads) and the
                  parsed summary changes, React remounts this input with the
                  fresh defaultValue. Otherwise it stays out of React's way so
                  the DM can type freely without a transform per keystroke —
                  the change is committed on blur. */}
              <input
                type="text"
                key={`summary:${slug}:${fm.frontmatter.summary ?? ''}`}
                defaultValue={fm.frontmatter.summary ?? ''}
                disabled={!!busy}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next === (fm.frontmatter.summary ?? '')) return;
                  void applyFrontmatter({
                    setSummary: true,
                    summary: next || null,
                  });
                }}
              />
            </label>

            <label>
              <span>Tags (comma-separated)</span>
              <input
                type="text"
                disabled={!!busy}
                defaultValue={(fm.frontmatter.tags ?? []).join(', ')}
                key={(fm.frontmatter.tags ?? []).join(',')}
                onBlur={(e) => {
                  const tags = e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter((s) => s.length > 0);
                  void applyFrontmatter({
                    setTags: true,
                    tags: tags.length === 0 ? null : tags,
                  });
                }}
              />
            </label>
          </fieldset>

          <div className="lore-editor__actions">
            <button
              type="button"
              className="lore-editor__publish"
              disabled={!!busy}
              onClick={onPublish}
              title="Save current draft and publish to the vault"
            >
              Publish
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={onSaveDirect}
              title="Skip the draft and write straight to the vault"
            >
              Save direct
            </button>
            {hasDraft && (
              <button
                type="button"
                disabled={!!busy}
                onClick={onDiscardDraft}
                title="Throw away the draft and reload from the vault"
              >
                Discard draft
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
